import * as DateUtil from '../../util/Date';
import * as React from 'react';
import moment from 'moment-timezone';

import { Calendar, Callout, DayOfWeek, DefaultButton, DirectionalHint, FocusTrapZone, IconButton } from '@fluentui/react';

export interface DateSelectorProps {
  buttonString?: string;
  calendarProps?: any;
  disabled?: boolean;
  displayFormat?: string;
  outputFormat?: string;
  showNudgeButtons?: boolean;
  defaultDate: Date | string;
  onChange?: (date: string) => void;
}

export interface DateSelectorState {
  selectedDate?: Date;
  showCalendar: boolean;
}

export class DateSelector extends React.Component<DateSelectorProps, DateSelectorState> {
  public static defaultProps: DateSelectorProps = {
    buttonString: 'Select a date',
    defaultDate: new Date(),
    outputFormat: 'MM/DD/YYYY',
  };

  private calendarButton = React.createRef<HTMLDivElement>();

  public constructor(props: DateSelectorProps) {
    super(props);

    this.state = {
      showCalendar: false,
      selectedDate: DateUtil.toDate(this.props.defaultDate),
    };
  }

  public componentDidUpdate(prevProps: DateSelectorProps) {
    if (this.props.defaultDate !== prevProps.defaultDate) {
      this.setState({
        selectedDate: (this.props.defaultDate && new Date(this.props.defaultDate)) || new Date(),
      });
    }
  }

  public render(): JSX.Element {
    return (
      <div className="flex flex-row flex-center justify-center">
        {this.props.showNudgeButtons && <IconButton iconProps={{ iconName: 'ChevronLeft' }} title="Previous Day" onClick={this._onPreviousClick} data-tid="btn_prevDay" />}
        <div ref={this.calendarButton}>
          <DefaultButton
            disabled={this.props.disabled}
            onClick={this._onClick}
            text={!this.state.selectedDate ? this.props.buttonString : moment(this.state.selectedDate).format(this.props.displayFormat || 'MM/DD/YY')}
          />
        </div>
        {this.props.showNudgeButtons && <IconButton iconProps={{ iconName: 'ChevronRight' }} title="Next Day" onClick={this._onNextClick} data-tid="btn_nextDay" />}
        {this.state.showCalendar && (
          <Callout
            isBeakVisible={false}
            gapSpace={0}
            doNotLayer={false}
            target={this.calendarButton.current}
            directionalHint={DirectionalHint.bottomLeftEdge}
            onDismiss={this._onDismiss}
            setInitialFocus={true}
          >
            <FocusTrapZone isClickableOutsideFocusTrap={true}>
              <Calendar
                onSelectDate={this._onSelectDate}
                onDismiss={this._onDismiss}
                value={this.state.selectedDate}
                firstDayOfWeek={DayOfWeek.Monday}
                strings={DateUtil.DatePickerStrings}
                showGoToToday={false}
                isMonthPickerVisible={false}
                {...this.props.calendarProps}
              />
            </FocusTrapZone>
          </Callout>
        )}
      </div>
    );
  }

  private _onClick = () => {
    this.setState({ showCalendar: !this.state.showCalendar });
  };

  private _onDismiss = () => {
    this.setState({ showCalendar: false });
  };

  private _onPreviousClick = () => {
    const day = new Date(this.state.selectedDate!);
    day.setDate(day.getDate() - 1);
    this._onSelectDate(day);
  };

  private _onNextClick = () => {
    const day = new Date(this.state.selectedDate!);
    day.setDate(day.getDate() + 1);
    this._onSelectDate(day);
  };

  private _onSelectDate = (date: Date) => {
    this.setState({ showCalendar: false, selectedDate: date }, () => {
      if (this.props.onChange) {
        const formattedDateString = moment(date).format(this.props.outputFormat);
        this.props.onChange(formattedDateString);
      }
    });
  };
}
